import { ChangeDetectionStrategy, Component, inject, OnInit, signal, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MsalService } from "@azure/msal-angular";

import { PortalConfig } from "@shared/portal-config";
import { UserSessionService } from "@shared/services/user-session.service";
import { ModalService } from "@shared/services/modal.service";
import { SessionStorageService } from "@shared/services/session-storage.service";
import { TermsModalComponent } from "@components/views/terms-modal/terms-modal.component";
import { AccessibilityModalComponent } from "@components/views/accessibility-modal/accessibility-modal.component";
import { TermsService } from "@shared/services/terms.service";
import { AccessibilityService } from "@shared/services/accessibility.service";
import { catchError, of } from 'rxjs';

import { AngularSvgIconModule } from 'angular-svg-icon';

@Component({
    selector: 'app-account-menu',
    templateUrl: './account-menu.component.html',
    styleUrls: ['./account-menu.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [AngularSvgIconModule]
})
export class AccountMenuComponent implements OnInit {

    readonly username = signal('');
    readonly emailAddress = signal('');
    termsAvailable = signal(false);
    accessibilityAvailable = signal(false);

    private readonly _config = inject(PortalConfig);
    private readonly _userSessionService = inject(UserSessionService);
    private readonly _modalService = inject(ModalService);
    private readonly _sessionStorageService = inject(SessionStorageService);
    private readonly _msalService = inject(MsalService);
    private readonly _termsService = inject(TermsService);
    private readonly _accessibilityService = inject(AccessibilityService);
    private readonly _destroyRef = inject(DestroyRef);

    ngOnInit(): void {
        const userSession = this._userSessionService.userSession;
        this.username.set(userSession.username);
        this.emailAddress.set(userSession.emailAddress);

        // Load terms and accessibility availability status from server
        this.loadTermsAvailability();
        this.loadAccessibilityAvailability();
    }

    private loadTermsAvailability(): void {
        this._termsService.getTermsContent().pipe(
            takeUntilDestroyed(this._destroyRef),
            catchError(error => {
                // Log the error for debugging
                console.error('Error loading terms status:', error);
                // Hide menu item if API call fails
                this.termsAvailable.set(false);
                return of(null);
            })
        ).subscribe(response => {
            if (response) {
                // Show menu item only if terms are enabled
                this.termsAvailable.set(response.isTermsEnabled ?? false);
            } else {
                // If response is null, hide the menu item
                this.termsAvailable.set(false);
            }
        });
    }

    private loadAccessibilityAvailability(): void {
        this._accessibilityService.getAccessibilityContent().pipe(
            takeUntilDestroyed(this._destroyRef),
            catchError(error => {
                // Log the error for debugging
                console.error('Error loading accessibility status:', error);
                // Hide menu item if API call fails
                this.accessibilityAvailable.set(false);
                return of(null);
            })
        ).subscribe(response => {
            if (response) {
                // Show menu item only if accessibility is enabled
                this.accessibilityAvailable.set(response.isAccessibilityEnabled ?? false);
            } else {
                // If response is null, hide the menu item
                this.accessibilityAvailable.set(false);
            }
        });
    }

    public openTermsAndConditionsModal(): void {
        this._modalService.openModal(TermsModalComponent);
    }

    public openAccessibilityModal(): void {
        this._modalService.openModal(AccessibilityModalComponent);
    }

    public logout(): void {
        this._userSessionService.logout();
    }
}
